/**
 * Currency Management Page
 * Allows super admins and authorized admins to adjust user emeralds/rubies
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import { 
  Search, 
  Plus, 
  Minus, 
  RefreshCw, 
  User, 
  History,
  Shield,
  Check,
  X,
  AlertCircle
} from 'lucide-react';


// ========================================================
// DAILY POST BONUS (round 6) — admin switch for the growth lever:
// one bonus per user per UTC day for posting at least once.
// ========================================================
const DailyBonusCard = () => {
  const { api } = useAdmin();
  const [cfg, setCfg] = useState(null);
  const [amountDraft, setAmountDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/admin/settings/daily-bonus')
      .then(r => { setCfg(r.data); setAmountDraft(String(r.data.emeralds)); })
      .catch(() => toast.error('Failed to load daily bonus settings'));
  }, []);

  const save = async (body) => {
    setSaving(true);
    try {
      const r = await api.put('/api/admin/settings/daily-bonus', body);
      setCfg(r.data);
      setAmountDraft(String(r.data.emeralds));
      toast.success(`Daily bonus ${r.data.enabled ? 'ON' : 'OFF'} · ${r.data.emeralds} emeralds`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!cfg) return null;
  return (
    <div className="mb-6 bg-white rounded-xl border border-gray-200 p-5" data-testid="daily-bonus-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-bold text-gray-900">Daily Post Bonus</h2>
          <p className="text-sm text-gray-600">
            Pays every user {cfg.emeralds} emeralds for their first post of the day. One per user per day (UTC).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min="0"
            max="1000"
            value={amountDraft}
            onChange={(e) => setAmountDraft(e.target.value)}
            onBlur={() => {
              const v = parseInt(amountDraft, 10);
              if (!Number.isNaN(v) && v !== cfg.emeralds) save({ emeralds: v });
            }}
            className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            data-testid="daily-bonus-amount"
          />
          <button
            disabled={saving}
            onClick={() => save({ enabled: !cfg.enabled })}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
              cfg.enabled ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
            data-testid="daily-bonus-toggle"
          >
            {cfg.enabled ? 'ON — click to turn off' : 'OFF — click to turn on'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ========================================================
// PRICING (Round 59) — GEMS Plus on the web, the iPhone display price, the
// Emerald packs and the recharge range. Saving the Plus price updates the
// PayPal plan (new subscribers pay it; existing ones keep theirs).
// ========================================================
const PricingCard = () => {
  const { api, admin } = useAdmin();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const superAdmin = admin?.role === 'super_admin';

  const load = async () => {
    try {
      const r = await api.get('/api/admin/settings/pricing');
      setData(r.data);
      setDraft({ ...r.data.pricing, emerald_packages: r.data.pricing.emerald_packages.map((p) => ({ price: p.price, bonus: p.bonus })) });
    } catch (e) { toast.error('Failed to load pricing'); }
  };
  useEffect(() => { load(); }, []);

  if (!data || !draft) return null;
  const rate = data.limits?.emeralds_per_usd || 100;
  const maxBonus = data.limits?.max_bonus || 0.25;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setPack = (i, k, v) => setDraft((d) => ({ ...d, emerald_packages: d.emerald_packages.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        plus_price_usd: parseFloat(draft.plus_price_usd), plus_price_ios_usd: parseFloat(draft.plus_price_ios_usd),
        min_recharge_usd: parseFloat(draft.min_recharge_usd), max_recharge_usd: parseFloat(draft.max_recharge_usd),
        emerald_packages: draft.emerald_packages.map((p) => ({ price: parseFloat(p.price), bonus: parseInt(p.bonus, 10) || 0 })),
      };
      const r = await api.put('/api/admin/settings/pricing', body);
      setData((d) => ({ ...d, pricing: r.data.pricing, paypal: r.data.paypal }));
      setDraft({ ...r.data.pricing, emerald_packages: r.data.pricing.emerald_packages.map((p) => ({ price: p.price, bonus: p.bonus })) });
      toast.success(r.data.paypal_note || 'Prices saved — the web and the apps read them on their next load', { duration: 10000 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed', { duration: 10000 }); }
    finally { setSaving(false); }
  };
  const sync = async () => {
    setSaving(true);
    try { const r = await api.post('/api/admin/settings/pricing/paypal-sync'); setData((d) => ({ ...d, paypal: r.data.paypal })); toast.success(`PayPal plan now $${r.data.price.toFixed(2)}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'PayPal sync failed', { duration: 10000 }); }
    finally { setSaving(false); }
  };
  const pp = data.paypal || {};
  const paypalLine = !pp.configured ? 'PayPal keys are not set on this server.'
    : !pp.plan_id_set ? 'PAYPAL_PLUS_PLAN_ID is not set — the Plus plan must be created in PayPal first.'
      : pp.error ? `PayPal could not be read: ${pp.error}`
        : pp.price == null ? 'PayPal plan found, but it shows no regular price.'
          : `PayPal plan (…${pp.plan_id_tail}, ${pp.mode}) charges $${pp.price.toFixed(2)} · ${pp.status || ''}${pp.in_sync ? ' · in sync' : ' · DIFFERS from the price below'}`;

  return (
    <div className="mb-6 bg-white rounded-xl border border-gray-200 p-5" data-testid="pricing-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-bold text-gray-900">Pricing</h2>
          <p className="text-sm text-gray-600">What GEMS charges, everywhere at once: the web, the apps and the PayPal plan read these. {superAdmin ? '' : 'Super admins can change them.'}</p>
        </div>
        <p className={`text-xs ${pp.in_sync ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="pricing-paypal">{paypalLine}</p>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-4">
        <label className="text-sm text-gray-700">GEMS Plus on the web ($ / month)
          <input type="number" step="0.01" min="0.99" max="99.99" disabled={!superAdmin} value={draft.plus_price_usd} onChange={(e) => set('plus_price_usd', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="pricing-plus" />
          <span className="block text-xs text-gray-400 mt-1">Saving a new price updates the PayPal plan. New subscribers pay it; existing ones keep theirs.</span>
        </label>
        <label className="text-sm text-gray-700">iPhone display price ($)
          <input type="number" step="0.01" min="0.99" max="99.99" disabled={!superAdmin} value={draft.plus_price_ios_usd} onChange={(e) => set('plus_price_ios_usd', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <span className="block text-xs text-gray-400 mt-1">Only what the app shows before the App Store answers. The real iPhone price is set in App Store Connect.</span>
        </label>
        <label className="text-sm text-gray-700">Smallest recharge ($)
          <input type="number" step="1" min="1" disabled={!superAdmin} value={draft.min_recharge_usd} onChange={(e) => set('min_recharge_usd', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="text-sm text-gray-700">Largest self-serve recharge ($)
          <input type="number" step="1" min="1" disabled={!superAdmin} value={draft.max_recharge_usd} onChange={(e) => set('max_recharge_usd', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </label>
      </div>
      <div className="mt-4">
        <p className="text-sm font-semibold text-gray-800">Emerald packs <span className="font-normal text-gray-500">· {rate} Emeralds per dollar · a bonus can be at most {Math.round(maxBonus * 100)}% of the pack</span></p>
        <table className="mt-2 w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500"><th className="py-1">Price ($)</th><th className="py-1">Emeralds</th><th className="py-1">Bonus Emeralds</th><th className="py-1">Shown as</th><th></th></tr></thead>
          <tbody>
            {draft.emerald_packages.map((p, i) => {
              const emeralds = Math.round((parseFloat(p.price) || 0) * rate);
              const bonus = parseInt(p.bonus, 10) || 0;
              const over = bonus > Math.floor(emeralds * maxBonus);
              return (
                <tr key={i} className="border-t">
                  <td className="py-1 pr-2"><input type="number" step="1" min="1" disabled={!superAdmin} value={p.price} onChange={(e) => setPack(i, 'price', e.target.value)} className="w-28 border border-gray-300 rounded-lg px-2 py-1" data-testid={`pricing-pack-price-${i}`} /></td>
                  <td className="py-1 pr-2 text-gray-700">{emeralds.toLocaleString()}</td>
                  <td className="py-1 pr-2"><input type="number" step="1" min="0" disabled={!superAdmin} value={p.bonus} onChange={(e) => setPack(i, 'bonus', e.target.value)} className={`w-28 border rounded-lg px-2 py-1 ${over ? 'border-red-400' : 'border-gray-300'}`} /></td>
                  <td className="py-1 pr-2 text-gray-500">{bonus ? `${Math.round((100 * bonus) / Math.max(1, emeralds))}% more` : '—'}{over && <span className="text-red-600"> · over the limit</span>}</td>
                  <td className="py-1 text-right">{superAdmin && <button onClick={() => setDraft((d) => ({ ...d, emerald_packages: d.emerald_packages.filter((_, j) => j !== i) }))} className="text-xs text-gray-400 hover:text-red-700">Remove</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {superAdmin && <button onClick={() => setDraft((d) => ({ ...d, emerald_packages: [...d.emerald_packages, { price: '', bonus: 0 }] }))} className="mt-2 text-xs text-emerald-700 underline">Add a pack</button>}
      </div>
      {superAdmin && (
        <div className="mt-4 flex items-center gap-3">
          <button disabled={saving} onClick={save} className="px-4 py-2 rounded-full text-sm font-bold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50" data-testid="pricing-save">{saving ? 'Saving…' : 'Save prices'}</button>
          {pp.configured && pp.plan_id_set && !pp.in_sync && <button disabled={saving} onClick={sync} className="px-4 py-2 rounded-full text-sm font-bold border border-gray-300 text-gray-800 hover:bg-gray-50 disabled:opacity-50" data-testid="pricing-sync">Sync the PayPal plan to this price</button>}
          {data.pricing.updated_at && <span className="text-xs text-gray-400">last saved {new Date(data.pricing.updated_at).toLocaleString()}</span>}
        </div>
      )}
    </div>
  );
};

// Emerald Icon Component
const EmeraldIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="emeraldGradientCurrency" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#34d399" />
        <stop offset="50%" stopColor="#10b981" />
        <stop offset="100%" stopColor="#059669" />
      </linearGradient>
    </defs>
    <path d="M12 2L4 7V17L12 22L20 17V7L12 2Z" fill="url(#emeraldGradientCurrency)" />
    <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="#6ee7b7" fillOpacity="0.6" />
    <path d="M12 12V22L4 17V7L12 12Z" fill="#059669" fillOpacity="0.4" />
  </svg>
);

// Ruby Icon Component
const RubyIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="rubyGradientCurrency" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f87171" />
        <stop offset="50%" stopColor="#ef4444" />
        <stop offset="100%" stopColor="#dc2626" />
      </linearGradient>
    </defs>
    <path d="M12 2L4 7V17L12 22L20 17V7L12 2Z" fill="url(#rubyGradientCurrency)" />
    <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="#fca5a5" fillOpacity="0.6" />
    <path d="M12 12V22L4 17V7L12 12Z" fill="#dc2626" fillOpacity="0.4" />
  </svg>
);

const CurrencyManagementPage = () => {
  const { api } = useAdmin();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [activeTab, setActiveTab] = useState('adjust'); // 'adjust', 'history', 'permissions'
  
  // Adjustment form state
  const [adjustForm, setAdjustForm] = useState({
    currencyType: 'emeralds',
    adjustmentType: 'add',
    amount: '',
    reason: ''
  });
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => {
    fetchPermissions();
    fetchTransactions();
    if (permissions?.is_super_admin) {
      fetchAdmins();
    }
  }, []);

  useEffect(() => {
    if (permissions?.is_super_admin) {
      fetchAdmins();
    }
  }, [permissions]);

  const fetchPermissions = async () => {
    try {
      const response = await api.get('/api/admin/me/permissions');
      setPermissions(response.data);
      
      if (!response.data.can_manage_currency) {
        toast.error('You do not have permission to manage currency');
      }
    } catch (error) {
      toast.error('Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/api/admin/admins/with-permissions');
      setAdmins(response.data.admins || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const fetchTransactions = async (userId = null) => {
    try {
      const params = userId ? `?user_id=${userId}` : '';
      const response = await api.get(`/api/admin/currency/transactions${params}`);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setUsers([]);
      return;
    }
    
    try {
      const response = await api.get(`/api/admin/users?query=${encodeURIComponent(searchQuery)}`);
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to search users');
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setShowAdjustModal(true);
    setAdjustForm({
      currencyType: 'emeralds',
      adjustmentType: 'add',
      amount: '',
      reason: ''
    });
  };

  const handleAdjustCurrency = async () => {
    if (!selectedUser || !adjustForm.amount || !adjustForm.reason) {
      toast.error('Please fill all fields');
      return;
    }

    const amount = parseFloat(adjustForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setAdjusting(true);
    try {
      const response = await api.post('/api/admin/currency/adjust', {
        user_id: selectedUser.id,
        currency_type: adjustForm.currencyType,
        adjustment_type: adjustForm.adjustmentType,
        amount: amount,
        reason: adjustForm.reason
      });

      toast.success(response.data.message);
      setShowAdjustModal(false);
      setSelectedUser(response.data.user);
      fetchTransactions();
      
      // Update user in list
      setUsers(users.map(u => u.id === response.data.user.id ? response.data.user : u));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust currency');
    } finally {
      setAdjusting(false);
    }
  };

  const handleTogglePermission = async (adminUserId, enabled) => {
    try {
      await api.put(`/api/admin/admins/${adminUserId}/permissions`, {
        admin_user_id: adminUserId,
        permission: 'can_manage_currency',
        enabled: enabled
      });
      
      toast.success(`Currency permission ${enabled ? 'granted' : 'revoked'}`);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permission');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!permissions?.can_manage_currency) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">You do not have permission to manage currency.</p>
          <p className="text-red-500 text-sm mt-2">Contact a Super Admin to request access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <EmeraldIcon className="h-7 w-7" />
            Currency Management
          </h1>
          <p className="text-gray-600">Adjust user emeralds and rubies</p>
        </div>
        <div className="flex items-center gap-2">
          {permissions?.is_super_admin && (
            <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium flex items-center gap-1">
              <Shield className="h-4 w-4" />
              Super Admin
            </span>
          )}
        </div>
      </div>

      {/* Round 6: growth lever — daily post bonus switch */}
      <DailyBonusCard />
      <PricingCard />

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('adjust')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'adjust' 
              ? 'border-emerald-500 text-emerald-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Adjust Currency
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'history' 
              ? 'border-emerald-500 text-emerald-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Transaction History
        </button>
        {permissions?.is_super_admin && (
          <button
            onClick={() => setActiveTab('permissions')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'permissions' 
                ? 'border-emerald-500 text-emerald-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Manage Permissions
          </button>
        )}
      </div>

      {/* Adjust Currency Tab */}
      {activeTab === 'adjust' && (
        <div className="space-y-6">
          {/* Search Users */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Find User</h2>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by email, name, or handle..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <button
                onClick={searchUsers}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Search
              </button>
            </div>

            {/* Search Results */}
            {users.length > 0 && (
              <div className="mt-4 divide-y divide-gray-100">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="py-3 flex items-center justify-between hover:bg-gray-50 px-2 rounded-lg cursor-pointer"
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1 text-emerald-600">
                        <EmeraldIcon className="h-4 w-4" />
                        <span className="font-medium">{(user.emeralds || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1 text-red-600">
                        <RubyIcon className="h-4 w-4" />
                        <span className="font-medium">{(user.rubies || 0).toLocaleString()}</span>
                      </div>
                      <button className="text-emerald-600 hover:text-emerald-700 font-medium">
                        Adjust →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transaction History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <History className="h-5 w-5" />
              Currency Adjustment History
            </h2>
            <button
              onClick={() => fetchTransactions()}
              className="p-2 text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">User</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Balance</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Reason</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Admin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  transactions.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(t.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{t.user?.name}</p>
                          <p className="text-xs text-gray-500">{t.user?.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          t.currency_type === 'emeralds' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {t.currency_type === 'emeralds' ? <EmeraldIcon className="h-3 w-3" /> : <RubyIcon className="h-3 w-3" />}
                          {t.currency_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-medium ${t.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {t.amount > 0 ? '+' : ''}{t.amount?.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {t.previous_balance?.toLocaleString()} → {t.new_balance?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {t.reason}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {t.admin?.name}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Permissions Tab (Super Admin Only) */}
      {activeTab === 'permissions' && permissions?.is_super_admin && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Admin Currency Permissions
            </h2>
            <p className="text-sm text-gray-500 mt-1">Grant or revoke currency management access for admins</p>
          </div>
          <div className="divide-y divide-gray-200">
            {admins.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No admin users found
              </div>
            ) : (
              admins.map((admin) => (
                <div key={admin.user?.id} className="px-4 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{admin.user?.name}</p>
                      <p className="text-sm text-gray-500">{admin.user?.email}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      admin.role === 'super_admin' 
                        ? 'bg-amber-100 text-amber-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {admin.role}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    {admin.role === 'super_admin' ? (
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Check className="h-4 w-4 text-emerald-500" />
                        Always has access
                      </span>
                    ) : (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-sm text-gray-600">Can Manage Currency</span>
                        <button
                          onClick={() => handleTogglePermission(admin.user?.id, !admin.can_manage_currency)}
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            admin.can_manage_currency ? 'bg-emerald-500' : 'bg-gray-300'
                          }`}
                        >
                          <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            admin.can_manage_currency ? 'left-7' : 'left-1'
                          }`} />
                        </button>
                      </label>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Adjust Modal */}
      {showAdjustModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Adjust Currency</h3>
              <button 
                onClick={() => setShowAdjustModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* User Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{selectedUser.name}</p>
                    <p className="text-sm text-gray-500">{selectedUser.email}</p>
                  </div>
                </div>
                <div className="flex gap-6 mt-3">
                  <div className="flex items-center gap-2">
                    <EmeraldIcon className="h-5 w-5" />
                    <span className="font-medium">{(selectedUser.emeralds || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <RubyIcon className="h-5 w-5" />
                    <span className="font-medium">{(selectedUser.rubies || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Currency Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Currency Type</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAdjustForm({...adjustForm, currencyType: 'emeralds'})}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                      adjustForm.currencyType === 'emeralds'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <EmeraldIcon className="h-5 w-5" />
                    Emeralds
                  </button>
                  <button
                    onClick={() => setAdjustForm({...adjustForm, currencyType: 'rubies'})}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                      adjustForm.currencyType === 'rubies'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <RubyIcon className="h-5 w-5" />
                    Rubies
                  </button>
                </div>
              </div>

              {/* Action Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAdjustForm({...adjustForm, adjustmentType: 'add'})}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                      adjustForm.adjustmentType === 'add'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Plus className="h-5 w-5" />
                    Add
                  </button>
                  <button
                    onClick={() => setAdjustForm({...adjustForm, adjustmentType: 'remove'})}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                      adjustForm.adjustmentType === 'remove'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Minus className="h-5 w-5" />
                    Remove
                  </button>
                </div>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={adjustForm.amount}
                  onChange={(e) => setAdjustForm({...adjustForm, amount: e.target.value})}
                  placeholder="Enter amount"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                <textarea
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({...adjustForm, reason: e.target.value})}
                  placeholder="Enter reason for adjustment (required)"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowAdjustModal(false)}
                className="flex-1 py-2 px-4 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAdjustCurrency}
                disabled={adjusting || !adjustForm.amount || !adjustForm.reason}
                className={`flex-1 py-2 px-4 rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2 ${
                  adjustForm.adjustmentType === 'add'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {adjusting ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : adjustForm.adjustmentType === 'add' ? (
                  <Plus className="h-4 w-4" />
                ) : (
                  <Minus className="h-4 w-4" />
                )}
                {adjustForm.adjustmentType === 'add' ? 'Add' : 'Remove'} {adjustForm.currencyType}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencyManagementPage;
